import { createContext, forwardRef, useCallback, useContext, useEffect, useRef, type ComponentProps, type MouseEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, type To } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import clsx from 'clsx';

/**
 * Where a detail page was opened from (Home widgets today; any list can provide one), so it can offer
 * "← Back to Home". Carried in navigation state, with `?from=<key>` as a fallback that survives a reload or a
 * shared link.
 */
export interface Origin { key: string; path: string; label: string }
export const ORIGINS: Record<string, Origin> = {
  home: { key: 'home', path: '/', label: 'Home' },
  calendar: { key: 'calendar', path: '/calendar', label: 'Calendar' },
  tasks: { key: 'tasks', path: '/projects', label: 'Tasks' },
  hauliers: { key: 'hauliers', path: '/fleet/hauliers', label: 'Hauliers' },
};
/** A building page as the origin: "← Back to Main Warehouse" (name from navigation state; "the building" after a reload). */
export const buildingOrigin = (id: string, name?: string): Origin => ({ key: 'building', path: `/buildings/${id}`, label: name ?? 'the building' });
/**
 * t615u: Settings as the origin. A page reached from a settings section ("Departments & groups", "Calendar
 * reminders") offers "← Back to Settings", returning to that section. `?from=settings:<group>/<item>` survives a reload.
 */
export function settingsOrigin(pathname = '/settings'): Origin {
  const m = /^\/settings\/([a-z0-9-]+)\/([a-z0-9-]+)/.exec(pathname);
  return m ? { key: `settings:${m[1]}/${m[2]}`, path: `/settings/${m[1]}/${m[2]}`, label: 'Settings' } : { key: 'settings', path: '/settings', label: 'Settings' };
}
export const isSettingsOrigin = (o: Origin | null | undefined) => Boolean(o && (o.key === 'settings' || o.key.startsWith('settings:')));
const originFromKey = (key: string): Origin | null => {
  if (key === 'settings') return settingsOrigin();
  if (key.startsWith('settings:')) return settingsOrigin(`/settings/${key.slice('settings:'.length)}`);
  return ORIGINS[key] ?? null;
};
const OriginContext = createContext<Origin | null>(null);

export function OriginProvider({ origin, children }: { origin: Origin; children: ReactNode }) {
  return <OriginContext.Provider value={origin}>{children}</OriginContext.Provider>;
}
export const useOriginSource = () => useContext(OriginContext);

/** Add the origin to a path: `?from=home` (kept on reload) alongside the navigation state. */
export function withFrom(to: string, origin: Origin | null): string {
  if (!origin || !to.startsWith('/') || to === origin.path) return to;
  const [path, hash = ''] = to.split('#');
  const url = new URL(path, 'http://x');
  url.searchParams.set('from', origin.key);
  return `${url.pathname}${url.search}${hash ? `#${hash}` : ''}`;
}
export const originState = (origin: Origin | null) => (origin ? { from: origin } : undefined);

/** A Link that remembers the page it was clicked on, when that page provides an origin. */
export const OriginLink = forwardRef<HTMLAnchorElement, ComponentProps<typeof Link>>(function OriginLink({ to, state, ...rest }, ref) {
  const origin = useOriginSource();
  const target: To = typeof to === 'string' ? withFrom(to, origin) : to;
  return <Link ref={ref} to={target} state={origin ? { ...(state as object | undefined), ...originState(origin) } : state} {...rest} />;
});

/**
 * For a page that provides an origin: plain in-app links inside it (widgets that use their own Link) also carry
 * it. Links that already have `from=` (OriginLink) and modified / new-tab clicks are left alone.
 */
export function captureOriginClick(e: MouseEvent, nav: (to: string, o: { state: unknown }) => void, origin: Origin = ORIGINS.home, skip?: (pathname: string) => boolean) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const a = (e.target as HTMLElement).closest?.('a[href]') as HTMLAnchorElement | null;
  if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
  // t163: a button inside a card link (a person's name) handles its own click; don't follow the card.
  const btn = (e.target as HTMLElement).closest?.('button');
  if (btn && a.contains(btn)) return;
  const url = new URL(a.href, window.location.href);
  if (url.origin !== window.location.origin || url.searchParams.has('from') || url.pathname === origin.path || skip?.(url.pathname)) return;
  e.preventDefault();
  nav(withFrom(`${url.pathname}${url.search}${url.hash}`, origin), { state: originState(origin) });
}

/** Navigate, remembering the origin (for buttons in widgets). */
export function useOriginNavigate() {
  const origin = useOriginSource();
  const nav = useNavigate();
  return useCallback((to: string) => nav(withFrom(to, origin), { state: originState(origin) }), [nav, origin]);
}

/** The origin of the current page, from navigation state or `?from=`. */
export function useOrigin(): Origin | null {
  const loc = useLocation();
  const st = (loc.state as { from?: Origin } | null)?.from;
  if (st?.path && st.label) return st;
  const sp = new URLSearchParams(loc.search);
  const key = sp.get('from');
  // ?from=building: the building is the list's own location_id filter (or ?building=), so a reload still goes back to it.
  if (key === 'building') { const id = sp.get('location_id') ?? sp.get('building'); return id ? buildingOrigin(id) : null; }
  return key ? originFromKey(key) : null;
}

/** Go back: browser Back when we arrived by an in-app click (restores the origin's scroll), else to its path. */
export function useBackToOrigin() {
  const origin = useOrigin();
  const loc = useLocation();
  const nav = useNavigate();
  const viaState = Boolean((loc.state as { from?: Origin } | null)?.from);
  return {
    origin,
    back: useCallback((e?: MouseEvent) => {
      if (!origin) return;
      e?.preventDefault();
      const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
      if (viaState && idx > 0) nav(-1); else nav(origin.path);
    }, [origin, viaState, nav]),
  };
}

/** "← Back to Home" (or another origin). Renders nothing without an origin. */
export function BackToOrigin({ className, compact }: { className?: string; compact?: boolean }) {
  const { origin, back } = useBackToOrigin();
  // t615u: "Back to Settings" is the shell's SettingsReturnBar (same place on every page, phone and computer).
  if (!origin || isSettingsOrigin(origin)) return null;
  return (
    <a href={origin.path} onClick={back} data-testid="back-to-origin" aria-label={compact ? `Back to ${origin.label}` : undefined}
      className={clsx('inline-flex items-center gap-1 rounded text-sm text-slate-600 hover:underline focus-visible:outline-2 dark:text-slate-300', className)}>
      <ArrowLeft className={compact ? 'h-5 w-5' : 'h-4 w-4'} aria-hidden />{!compact && <>Back to {origin.label}</>}
    </a>
  );
}

/**
 * Browser / phone Back closes an open drawer instead of leaving the page underneath (so a drawer opened from
 * Home closes back to Home). Pushes one history entry while open; closing by other means pops it again.
 */
export function useBackClosesDrawer(open: boolean, close: () => void, key: string | true = true) {
  const pushed = useRef(false);
  const closeRef = useRef(close);
  closeRef.current = close;
  const keyRef = useRef(key);
  keyRef.current = key;
  useEffect(() => {
    if (!open) return;
    const url = window.location.href;
    // Reopened by coming Back to the drawer's own entry (useReopenDrawerOnBack): it's already there, don't stack another.
    if ((window.history.state as { drawer?: unknown } | null)?.drawer !== keyRef.current) window.history.pushState({ ...(window.history.state as object | null), drawer: keyRef.current }, '', url);
    pushed.current = true;
    const onPop = () => { if (pushed.current) { pushed.current = false; closeRef.current(); } };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Closed by the X / Escape / a link: drop our entry if it's still on top and we haven't navigated.
      // Deferred, so a navigation made while closing (e.g. "Message" in the drawer) wins and we leave history alone.
      if (pushed.current) setTimeout(() => { if ((window.history.state as { drawer?: boolean } | null)?.drawer && window.location.href === url) window.history.back(); }, 0);
      pushed.current = false;
    };
  }, [open]);
  // Same drawer, different subject (another person): relabel our entry instead of closing and reopening it (t147u).
  useEffect(() => {
    if (!open || !pushed.current) return;
    const st = window.history.state as { drawer?: unknown } | null;
    if (st?.drawer && st.drawer !== key) window.history.replaceState({ ...st, drawer: key }, '', window.location.href);
  }, [open, key]);
}

/**
 * Open something else in the same drawer from inside it (a kudos giver from "Kudos for Priya"), so Back returns to
 * where you were: the current entry is relabelled `from` (e.g. person:<id>:kudos) and a new entry `to` goes on top.
 */
export function pushDrawerEntry(from: string, to: string) {
  const st = window.history.state as object | null;
  window.history.replaceState({ ...st, drawer: from }, '', window.location.href);
  window.history.pushState({ ...st, drawer: to }, '', window.location.href);
}

/**
 * t137u: a link inside a drawer (a building chip in the person drawer) navigates away, leaving the drawer's history
 * entry behind it. Coming Back to that entry reopens the drawer, so "Back" from the page returns to the drawer.
 */
export function useReopenDrawerOnBack(prefix: string, reopen: (id: string) => void) {
  const ref = useRef(reopen);
  ref.current = reopen;
  useEffect(() => {
    const onPop = () => {
      const d = (window.history.state as { drawer?: unknown } | null)?.drawer;
      if (typeof d === 'string' && d.startsWith(prefix)) ref.current(d.slice(prefix.length));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [prefix]);
}

/** A detail page's own back link ("All issues"), replaced by "Back to Home" when the page was opened from an origin. */
export function OriginBackLink({ to, label, className }: { to: string; label: string; className?: string }) {
  const { origin } = useBackToOrigin();
  const cls = clsx('inline-flex items-center gap-1 text-sm text-slate-500 hover:underline', className);
  if (origin && !isSettingsOrigin(origin)) return <BackToOrigin className={cls} />;
  return <Link to={to} className={cls}><ArrowLeft className="h-4 w-4" aria-hidden />{label}</Link>;
}

/**
 * t615u: a page opened from Settings (Departments & groups, Channel forms, Calendar reminders…) shows a compact
 * "← Back to Settings" strip at the top, returning to the exact section it was opened from. Rendered by the
 * computer and phone shells; nothing on other pages or inside Settings itself.
 */
export function SettingsReturnBar() {
  const { origin, back } = useBackToOrigin();
  const { pathname } = useLocation();
  if (!isSettingsOrigin(origin) || pathname === '/settings' || pathname.startsWith('/settings/')) return null;
  return (
    <div className="flex shrink-0 items-center border-b border-slate-200 bg-slate-50 px-4 py-1.5 sm:px-6 dark:border-slate-800 dark:bg-slate-900" data-testid="settings-return-bar">
      <a href={origin!.path} onClick={back} data-testid="back-to-settings"
        className="inline-flex items-center gap-1 rounded text-sm text-slate-700 hover:underline focus-visible:outline-2 dark:text-slate-200">
        <ArrowLeft className="h-4 w-4" aria-hidden />Back to Settings
      </a>
    </div>
  );
}
